import { HTMLAttributes } from 'react'
import Image from 'next/image'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { useImageTextMotion } from './motion'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

type ImageTextProps = HTMLAttributes<HTMLDivElement> & {
  pretitle: string
  title: string
  text: string
  image: WPImage
  alignment: 'image_left' | 'image_right'
  is_indented: boolean
  className?: string
}

const ImageText = ({
  pretitle,
  title,
  text,
  image,
  alignment = 'image_left',
  is_indented = false,
  className,
  ...props
}: ImageTextProps) => {
  const isRight = alignment === 'image_right'

  const imageClasses = cn(
    'overflow-clip rounded-radius-minimal',
    'col-span-2 md:col-span-2 lg:col-span-3',
    isRight && 'md:order-2 md:col-start-5',
    is_indented && isRight && 'lg:col-start-9',
    is_indented && !isRight && 'lg:col-start-2',
    !is_indented && isRight && 'lg:col-start-10',
    !is_indented && !isRight && 'lg:col-start-1'
  )

  const textClasses = cn(
    'grid col-span-2 md:col-span-4',
    !is_indented && !isRight && 'lg:col-span-8 lg:col-start-5',
    is_indented && !isRight && 'lg:col-span-6 lg:col-start-6',
    !is_indented && isRight && 'lg:col-span-8',
    is_indented && isRight && 'lg:col-span-6 lg:col-start-2'
  )

  const { motionRef } = useImageTextMotion()

  return (
    <Section
      ref={motionRef}
      className={cn('overflow-clip', className)}
      data-image-text={`${alignment}_${is_indented ? 'indented' : 'full'}`}
      {...props}
    >
      <Container>
        <Grid className="gap-y-spacing-sm">
          <div className={imageClasses} data-motion-image-col>
            <Image
              src={image.url}
              alt={image.alt || ''}
              width={image.width}
              height={image.height}
              className="h-full w-full object-cover"
              data-motion-image
            />
          </div>

          <div className={textClasses} data-motion-text-col>
            <div
              className={cn('col-[1/-1] row-[1/-1]', 'rounded-radius-minimal bg-white-light/20')}
              data-motion-background
            />
            <div
              className={cn(
                'col-[1/-1] row-[1/-1]',
                'grid gap-spacing-sm',
                'px-spacing-sm py-spacing-lg md:px-spacing-lg'
              )}
            >
              {pretitle && (
                <Typography variant="body" data-motion-pretitle>
                  {pretitle}
                </Typography>
              )}

              {title && (
                <Typography
                  variant="h6"
                  component="h2"
                  className="[&_.line-mask]:-mt-2 [&_.line]:pt-2"
                  data-motion-title
                >
                  {title}
                </Typography>
              )}

              {text && (
                <Typography variant="body" data-motion-paragraph>
                  {text}
                </Typography>
              )}
            </div>
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default ImageText
